import { Archive, ArrowLeft, Inbox, MailOpen, Paperclip, RefreshCw, Reply, Search, Star, Trash2, Users } from "lucide-react";
import type { JSX } from "react";

import { ApiRequestError } from "../../../common/api/apiClient.js";
import type { MailAccountDTO, MailMessageDTO, MailMessageSummaryDTO } from "../../../common/api/types.js";
import type { ModulePageProps } from "../../ModuleDefinition.js";
import { ComposeDialog } from "../components/ComposeDialog.js";
import { MailHtmlView } from "../components/MailHtmlView.js";
import { useMailLocation } from "../mailLocation.js";
import { viewLabel, type MailView } from "../mailTypes.js";
import { useDeleteMailMessage, useMailAccounts, useMailMessage, useMailMessages, useUpdateMailMessage } from "../query/mailQueries.js";

type Address = MailMessageSummaryDTO["to"][number];

export function MailPage(_props: ModulePageProps): JSX.Element {
  const accounts = useMailAccounts();
  const location = useMailLocation();
  const account = accounts.data?.find((entry) => entry.id === location.accountId) ?? accounts.data?.[0] ?? null;
  const messages = useMailMessages(account?.id ?? null, location.folder, location.query);
  const selected = useMailMessage(account?.id ?? null, location.messageId);
  const update = useUpdateMailMessage();
  const remove = useDeleteMailMessage();

  if (accounts.error) return <Unavailable error={accounts.error} />;
  if (accounts.isSuccess && !account) return <Unavailable error={null} />;

  const patch = (messageId: string, body: { flagged?: boolean; moveTo?: "archive" | "trash" | "inbox" }) => account && update.mutate({ accountId: account.id, messageId, body });
  const trash = (messageId: string) => {
    if (!account) return;
    // Im Papierkorb heißt Löschen endgültig, sonst wandert die Nachricht dorthin.
    if (location.folder === "trash") remove.mutate({ accountId: account.id, messageId });
    else patch(messageId, { moveTo: "trash" });
    location.openMessage(null);
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-surface-raised">
      {location.messageId ? (
        <MessageDetail
          message={selected.data ?? null}
          loading={selected.isPending}
          error={selected.error}
          onBack={() => location.openMessage(null)}
          onStar={(message) => patch(message.id, { flagged: !message.flagged })}
          onArchive={(message) => { patch(message.id, { moveTo: "archive" }); location.openMessage(null); }}
          onTrash={(message) => trash(message.id)}
          onReply={(message) => location.openComposer({ reply: message.id })}
        />
      ) : (
        <MessageList
          account={account}
          view={location.folder}
          messages={messages.data?.messages ?? []}
          total={messages.data?.total ?? 0}
          loading={messages.isPending}
          refreshing={messages.isFetching}
          error={messages.error}
          query={location.query}
          onQuery={location.setQuery}
          onRefresh={() => void messages.refetch()}
          onOpen={(message) => (message.draft ? location.openComposer({ draft: message.id }) : location.openMessage(message.id))}
          onStar={(message) => patch(message.id, { flagged: !message.flagged })}
        />
      )}
      {location.compose && account && accounts.data ? (
        <ComposeDialog
          accounts={accounts.data}
          accountId={account.id}
          draftId={location.draftId}
          replyTo={location.replyTo}
          onClose={location.closeComposer}
          onSent={() => location.closeComposer()}
        />
      ) : null}
    </div>
  );
}

function Unavailable({ error }: { error: Error | null }): JSX.Element {
  const noMailbox = !error || (error instanceof ApiRequestError && error.status === 403);
  return (
    <div className="grid flex-1 place-items-center bg-surface-raised p-8 text-center">
      <div className="max-w-sm">
        <Inbox className="mx-auto h-10 w-10 text-ink-muted" />
        <h2 className="mt-3 font-bold text-ink">{noMailbox ? "Noch kein Postfach" : "Mail ist gerade nicht erreichbar"}</h2>
        <p className="mt-1 text-sm text-ink-muted">
          {noMailbox
            ? "Für deine Anmeldeadresse gibt es kein Postfach. Ein Postfach entsteht für Adressen auf einer bestätigten Domain eures Unternehmens."
            : error?.message}
        </p>
      </div>
    </div>
  );
}

function MessageList({ account, view, messages, total, loading, refreshing, error, query, onQuery, onRefresh, onOpen, onStar }: {
  account: MailAccountDTO | null;
  view: MailView;
  messages: MailMessageSummaryDTO[];
  total: number;
  loading: boolean;
  refreshing: boolean;
  error: Error | null;
  query: string;
  onQuery: (value: string) => void;
  onRefresh: () => void;
  onOpen: (message: MailMessageSummaryDTO) => void;
  onStar: (message: MailMessageSummaryDTO) => void;
}): JSX.Element {
  const outgoing = view === "sent" || view === "drafts";
  return <>
    <header className="border-b border-line px-3 py-3 sm:px-5">
      <div className="flex items-center gap-3">
        <div className="min-w-0 flex-1">
          <h2 className="text-lg font-extrabold text-ink">{viewLabel(view)}</h2>
          <p className="flex items-center gap-1 truncate text-xs text-ink-muted">{account && !account.personal ? <Users className="h-3.5 w-3.5 shrink-0" /> : null}{account?.address}{loading ? "" : ` · ${total} Nachrichten`}</p>
        </div>
        <button type="button" aria-label="Aktualisieren" onClick={onRefresh} className="grid h-10 w-10 place-items-center rounded-lg text-ink-muted hover:bg-surface-sunken"><RefreshCw className={refreshing ? "h-4 w-4 animate-spin" : "h-4 w-4"} /></button>
      </div>
      <label className="relative mt-3 block max-w-2xl"><Search className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-ink-muted" /><input value={query} onChange={(event) => onQuery(event.target.value)} placeholder="In diesem Postfach suchen" className="min-h-10 w-full rounded-xl border border-line bg-surface-sunken py-2 pl-9 pr-3 text-sm text-ink outline-none focus:border-brand" /></label>
    </header>
    <div className="min-h-0 flex-1 overflow-y-auto">
      {error ? <p className="p-6 text-sm text-danger">{error.message}</p> : null}
      {loading ? <p className="p-6 text-sm text-ink-muted">Nachrichten werden geladen …</p> : null}
      {!loading && !error && messages.length === 0 ? <div className="grid h-full place-items-center p-8 text-center"><div><MailOpen className="mx-auto h-10 w-10 text-ink-muted" /><h3 className="mt-3 font-bold text-ink">Hier ist alles erledigt</h3><p className="mt-1 text-sm text-ink-muted">In diesem Bereich gibt es keine Nachrichten.</p></div></div> : null}
      <ul>
        {messages.map((message) => {
          const unread = !message.seen && !outgoing;
          return (
            <li key={message.id} className={unread ? "border-b border-line bg-brand/[0.045]" : "border-b border-line"}>
              <div className="group flex min-h-[4.75rem] items-center gap-2 px-2 hover:bg-surface-sunken sm:px-4">
                <button type="button" aria-label={message.flagged ? "Markierung entfernen" : "Markieren"} onClick={() => onStar(message)} className={message.flagged ? "grid h-10 w-10 shrink-0 place-items-center text-amber-500" : "grid h-10 w-10 shrink-0 place-items-center text-ink-muted hover:text-amber-500"}><Star className="h-4 w-4" fill={message.flagged ? "currentColor" : "none"} /></button>
                <button type="button" onClick={() => onOpen(message)} className="grid min-w-0 flex-1 grid-cols-[minmax(0,1fr)_auto] gap-x-3 py-3 text-left sm:grid-cols-[minmax(9rem,0.35fr)_minmax(0,1fr)_auto]">
                  <span className={unread ? "truncate text-sm font-extrabold text-ink" : "truncate text-sm font-medium text-ink"}>{outgoing ? `An: ${message.to.map(displayName).join(", ") || "Noch ohne Empfänger"}` : displayName(message.from)}</span>
                  <span className="col-start-1 row-start-2 truncate text-sm sm:col-start-2 sm:row-start-1"><b className={unread ? "font-bold text-ink" : "font-semibold text-ink"}>{message.draft ? <span className="text-danger">Entwurf · </span> : null}{message.subject || "(Kein Betreff)"}</b><span className="text-ink-muted"> – {message.preview}</span></span>
                  <span className="col-start-2 row-span-2 row-start-1 flex items-center gap-1 whitespace-nowrap text-xs font-semibold text-ink-muted sm:col-start-3">{message.hasAttachments ? <Paperclip className="h-3.5 w-3.5" /> : null}{formatListDate(message.receivedAt)}</span>
                </button>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  </>;
}

function MessageDetail({ message, loading, error, onBack, onStar, onArchive, onTrash, onReply }: {
  message: MailMessageDTO | null;
  loading: boolean;
  error: Error | null;
  onBack: () => void;
  onStar: (message: MailMessageDTO) => void;
  onArchive: (message: MailMessageDTO) => void;
  onTrash: (message: MailMessageDTO) => void;
  onReply: (message: MailMessageDTO) => void;
}): JSX.Element {
  const button = "grid h-10 w-10 place-items-center rounded-lg text-ink-muted hover:bg-surface-sunken disabled:opacity-40";
  return (
    <div className="min-h-0 flex-1 overflow-y-auto">
      <header className="sticky top-0 z-10 flex min-h-14 items-center gap-1 border-b border-line bg-surface-raised/95 px-2 backdrop-blur sm:px-4">
        <button type="button" onClick={onBack} aria-label="Zur Nachrichtenliste" className={button}><ArrowLeft className="h-4 w-4" /></button>
        <button type="button" disabled={!message} onClick={() => message && onArchive(message)} aria-label="Archivieren" className={button}><Archive className="h-4 w-4" /></button>
        <button type="button" disabled={!message} onClick={() => message && onTrash(message)} aria-label="Löschen" className={button}><Trash2 className="h-4 w-4" /></button>
        <button type="button" disabled={!message} onClick={() => message && onStar(message)} aria-label="Markieren" className={message?.flagged ? "grid h-10 w-10 place-items-center rounded-lg text-amber-500" : button}><Star className="h-4 w-4" fill={message?.flagged ? "currentColor" : "none"} /></button>
      </header>
      {loading ? <p className="p-6 text-sm text-ink-muted">Nachricht wird geladen …</p> : null}
      {error ? <p className="p-6 text-sm text-danger">{error.message}</p> : null}
      {message ? (
        <article className="mx-auto max-w-3xl px-4 py-6 sm:px-8">
          <div className="flex items-start gap-3">
            <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-brand/10 text-sm font-extrabold text-brand">{initials(displayName(message.from))}</span>
            <div className="min-w-0 flex-1">
              <h2 className="text-xl font-extrabold leading-tight text-ink sm:text-2xl">{message.subject || "(Kein Betreff)"}</h2>
              <div className="mt-4 flex flex-wrap items-baseline gap-x-2"><b className="text-sm text-ink">{displayName(message.from)}</b>{message.from ? <span className="text-xs text-ink-muted">&lt;{message.from.email}&gt;</span> : null}<time className="ml-auto text-xs text-ink-muted">{new Date(message.receivedAt).toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" })}</time></div>
              <p className="mt-0.5 text-xs text-ink-muted">an {message.to.map(displayName).join(", ") || "mich"}{message.cc.length ? ` · Cc ${message.cc.map(displayName).join(", ")}` : ""}</p>
            </div>
          </div>
          {message.html ? <MailHtmlView html={message.html} /> : <div className="mt-7 whitespace-pre-wrap break-words text-[15px] leading-7 text-ink">{message.text}</div>}
          {message.attachments.length ? <div className="mt-8 flex flex-wrap gap-2">{message.attachments.map((attachment) => <span key={attachment.blobId} className="flex min-h-11 items-center gap-2 rounded-xl border border-line bg-surface-sunken px-3 py-2 text-left"><Paperclip className="h-4 w-4 text-brand" /><span><b className="block text-xs text-ink">{attachment.name}</b><small className="text-ink-muted">{formatSize(attachment.sizeBytes)}</small></span></span>)}</div> : null}
          <button type="button" onClick={() => onReply(message)} className="mt-8 inline-flex min-h-11 items-center gap-2 rounded-xl border border-line px-4 py-2 text-sm font-bold text-ink hover:bg-surface-sunken"><Reply className="h-4 w-4" />Antworten</button>
        </article>
      ) : null}
    </div>
  );
}

function displayName(address: Address | null | undefined): string {
  return address ? address.name || address.email : "Unbekannt";
}
function formatListDate(value: string): string { const date = new Date(value); return date.toDateString() === new Date().toDateString() ? date.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" }) : date.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" }); }
function formatSize(bytes: number): string { return bytes >= 1_000_000 ? `${(bytes / 1_000_000).toLocaleString("de-DE", { maximumFractionDigits: 1 })} MB` : `${Math.max(1, Math.round(bytes / 1000))} KB`; }
function initials(name: string): string { return name.split(/\s+/).slice(0, 2).map((part) => part[0]).join("").toUpperCase(); }
